import { useLocation, Navigate, Outlet } from "react-router-dom"
import { selectCurrentRole, selectCurrentId } from "./authSlice"
import { useSelector, useDispatch } from "react-redux"
import { useRefreshMutation } from "./authApiSlice"
import { useEffect, useState } from "react"
import { showSpinner, hideSpinner } from "../../components/componentSlice"
import { useGetCrmIdByAdvisorIdMutation } from "../crm/crmApiSlice"
import { setCrm } from "../crm/crmSlice"

const RequireAuth = ({ allowedRoles }) => {
    const location = useLocation()
    const dispatch = useDispatch()

    const id = useSelector(selectCurrentId)
    const role = useSelector(selectCurrentRole)

    const [refresh, {
        isUninitialized,
        isLoading: isRefreshLoading,
        isError: isRefreshError
    }] = useRefreshMutation()

    const [
        getCrmIdByAdvisorId,
        {
            isLoading: isGetCrmLoading
        }
    ] = useGetCrmIdByAdvisorIdMutation()

    const [authChecked, setAuthChecked] = useState(false)
    const [crmChecked, setCrmChecked] = useState(false)

    /*
     * Restore authentication if Redux is empty.
     */
    useEffect(() => {
        const verifyRefreshToken = async () => {
            try {
                await refresh()
            } catch (err) {
                console.log("Refresh failed:", err)
            } finally {
                setAuthChecked(true)
            }
        }

        if (id && role) {
            // Already authenticated
            setAuthChecked(true)
            return
        }

        if (isUninitialized) {
            verifyRefreshToken()
        }
    }, [id, role, isUninitialized, refresh])

    /*
     * Get CRM for advisors.
     */
    useEffect(() => {
        const getAdvisorCrm = async () => {
            if (role !== "ADVISOR" || !id) {
                setCrmChecked(true)
                return
            }

            try {
                const crm = await getCrmIdByAdvisorId(id).unwrap()

                if (crm?.id) {
                    dispatch(setCrm(crm.id))
                }
            } catch (err) {
                console.log("Failed to get advisor CRM:", err)
            } finally {
                setCrmChecked(true)
            }
        }

        if (authChecked && id && role) {
            getAdvisorCrm()
        }
    }, [
        authChecked,
        id,
        role,
        getCrmIdByAdvisorId,
        dispatch
    ])

    /*
     * Show spinner while authentication / CRM is loading.
     */
    useEffect(() => {
        if (isRefreshLoading || isGetCrmLoading) {
            dispatch(showSpinner())
        } else {
            dispatch(hideSpinner())
        }
    }, [
        isRefreshLoading,
        isGetCrmLoading,
        dispatch
    ])

    /*
     * Wait until authentication has been checked.
     */
    if (!authChecked || isRefreshLoading) {
        return null
    }

    /*
     * Authentication failed.
     */
    if (isRefreshError || !id || !role) {
        return (
            <Navigate
                to="/"
                state={{ from: location }}
                replace
            />
        )
    }

    /*
     * User doesn't have permission for this route.
     */
    if (!allowedRoles.includes(role)) {
        return (
            <Navigate
                to="/"
                state={{ from: location }}
                replace
            />
        )
    }

    /*
     * Advisor CRM is still being retrieved.
     */
    if (role === "ADVISOR" && !crmChecked) {
        return null
    }

    return <Outlet />
}

export default RequireAuth